/**
 * Pair With Section JavaScript
 * Handles size selection and add to cart functionality for the Pair With section
 */

class PairWithSection {
  constructor() {
    this.init()
  }

  init() {
    this.bindSizeSelectors()
    this.bindAddToCartButtons()
  }

  bindSizeSelectors() {
    const sizeDropdowns = document.querySelectorAll('.size-dropdown')
    
    sizeDropdowns.forEach(dropdown => {
      dropdown.addEventListener('change', (e) => {
        this.handleSizeChange(e.target)
      })
    })
  }

  bindAddToCartButtons() {
    const addToCartButtons = document.querySelectorAll('.pair-with-product-card__add-to-cart')
    
    addToCartButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault()
        this.handleAddToCart(e.target)
      })
    })
  }

  handleSizeButtonClick(button) {
    const productSuffix = button.dataset.productSuffix
    const selectedSize = button.dataset.size
    const variantId = button.dataset.variantId
    
    // Remove selected class from all buttons in this product group
    const productGroup = button.closest('.size-buttons')
    const allButtons = productGroup.querySelectorAll('.size-button')
    allButtons.forEach(btn => btn.classList.remove('size-button--selected'))
    
    // Add selected class to clicked button
    button.classList.add('size-button--selected')
    
    // If we have variant ID directly, use it
    if (variantId) {
      this.handleVariantChange(variantId, productSuffix)
    } else {
      // Update variant and price using size
      this.handleSizeChange({
        dataset: {
          productId: button.dataset.productId,
          productSuffix: productSuffix
        },
        value: selectedSize
      })
    }
  }

  async handleSizeChange(selector) {
    const productId = selector.dataset.productId
    const productSuffix = selector.dataset.productSuffix
    const selectedValue = selector.value
    
    // Update size label to show selected value
    this.updateSizeLabel(selector, selectedValue)
    
    // Check if the value is a variant ID (numeric) or size option
    const isVariantId = !isNaN(selectedValue)
    
    if (isVariantId) {
      // Direct variant ID selection
      this.handleVariantChange(selectedValue, productSuffix)
    } else {
      // Size option selection - need to find variant
      // Try to get product data from Shopify's global object first
      let product = window.Shopify?.products?.[productId]
      
      // If not available, fetch product data
      if (!product) {
        try {
          const response = await fetch(`/products/${productId}.js`)
          product = await response.json()
        } catch (error) {
          console.error('Error fetching product data:', error)
          return
        }
      }
      
      // Find the corresponding variant based on selected size
      const variant = product.variants.find(v => 
        v.options.includes(selectedValue)
      )
      
      if (variant) {
        this.handleVariantChange(variant.id, productSuffix)
      }
    }
  }

  updateSizeLabel(selector, selectedValue) {
    // Find the label associated with this dropdown
    const sizeSelector = selector.closest('.pair-with-product-card__size-selector')
    if (sizeSelector) {
      const label = sizeSelector.querySelector('.size-label')
      const labelValue = label?.querySelector('.size-label__value')
      
      if (labelValue) {
        labelValue.textContent = selectedValue
      } else if (label) {
        // Fallback: update the entire label text
        const labelText = label.textContent.split(':')[0]
        label.textContent = labelText + ': ' + selectedValue
      }
    }
  }

  handleVariantChange(variantId, productSuffix) {
    // Update the add to cart button with new variant info
    const addToCartButton = document.querySelector(
      `[data-product-suffix="${productSuffix}"].pair-with-product-card__add-to-cart`
    )
    
    if (addToCartButton) {
      addToCartButton.dataset.variantId = variantId
      
      // We need to get variant info to check availability and price
      // For now, we'll assume it's available and let the add to cart handle the rest
      addToCartButton.disabled = false
      addToCartButton.textContent = 'Sepete ekle'
    }
  }

  handleAddToCart(button) {
    const variantId = button.dataset.variantId
    const productId = button.dataset.productId
    
    if (!variantId || button.disabled) return
    
    // Add to cart using Shopify's cart API
    this.addToCart(variantId, 1)
  }

  async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      })
      
      if (response.ok) {
        const cartData = await response.json()
        
        // Show success feedback
        this.showAddToCartFeedback(true)
        
        // Dispatch cart update event to update cart icon and drawer
        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: {
            data: {
              itemCount: cartData.item_count,
              source: 'pair-with-section'
            }
          }
        }))
        
        // Also dispatch the theme's cart update event
        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: {
            data: {
              itemCount: cartData.item_count,
              source: 'pair-with-section'
            }
          }
        }))
        
        // Update cart count if cart icon exists
        this.updateCartCount()
        
        // Open cart drawer if it exists
        this.openCartDrawer()
      } else {
        this.showAddToCartFeedback(false)
      }
    } catch (error) {
      console.error('Error adding to cart:', error)
      this.showAddToCartFeedback(false)
    }
  }

  showAddToCartFeedback(success) {
    // Create a temporary feedback message
    const feedback = document.createElement('div')
    feedback.className = `add-to-cart-feedback ${success ? 'success' : 'error'}`
    feedback.textContent = success ? 'Sepete eklendi!' : 'Sepete eklenemedi'
    
    // Style the feedback
    Object.assign(feedback.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '4px',
      color: 'white',
      backgroundColor: success ? '#28a745' : '#dc3545',
      zIndex: '9999',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
    })
    
    document.body.appendChild(feedback)
    
    // Remove after 3 seconds
    setTimeout(() => {
      feedback.remove()
    }, 3000)
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js')
      const cart = await response.json()
      
      // Update cart count in header if it exists
      const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]')
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count
      })
    } catch (error) {
      console.error('Error updating cart count:', error)
    }
  }

  openCartDrawer() {
    // Try to open cart drawer if it exists
    const cartDrawer = document.querySelector('cart-drawer-component')
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open()
    }
    
    // Also try to trigger cart drawer via click event
    const cartIcon = document.querySelector('.header-actions__action.action__cart')
    if (cartIcon) {
      cartIcon.click()
    }
  }

  formatMoney(cents) {
    // Use Shopify's money formatting if available, otherwise fallback
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents)
    }
    
    // Fallback formatting for Turkish Lira
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(cents / 100)
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new PairWithSection()
})

// Also initialize if the script loads after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PairWithSection()
  })
} else {
  new PairWithSection()
}
